/* istanbul ignore file */
import React from 'react';
import { ShowSection, SectionRow, BigSectionRow, ShowPageLayout } from 'framework/ui/layout';
import { TextField, EnumField, DateField } from 'framework/ui/field';
import {
  PURCHASE_ORDER_STATUSES_OPTIONS,
  PURCHASE_ORDER_STATUSES,
  PURCHASE_ORDER_TYPES,
} from 'domain/finance/purchaseOrder/usePurchaseOrderService';
import PurchaseOrderHeader from 'domain/finance/purchaseOrder/shared/PurchaseOrderHeader';
import RejectPurchaseOrderButton from 'domain/finance/purchaseOrder/shared/button/RejectPurchaseOrderButton';
import CopyPurchaseOrderButton from 'domain/finance/purchaseOrder/shared/button/CopyPurchaseOrderButton';
import PurchaseOrderTypeField from 'domain/finance/purchaseOrder/shared/field/PurchaseOrderTypeField';
import ApprovePurchaseOrderButton from 'domain/finance/purchaseOrder/shared/button/ApprovePurchaseOrderButton';
import ClosePurchaseOrderButton from 'domain/finance/purchaseOrder/shared/button/ClosePurchaseOrderButton';
import EditPurchaseOrderButton from 'domain/finance/purchaseOrder/shared/button/EditPurchaseOrderButton';
import SinglePurchaseOrderBreadcrumbs from 'domain/finance/purchaseOrder/shared/SinglePurchaseOrderBreadcrumbs';
import TransfersListField from 'domain/finance/shared/field/TransfersListField';
import { BUSINESS_OBJECT_TYPES } from 'domain/finance/ledger/useLedgerService';
import { usePurchaseOrderId } from 'domain/finance/purchaseOrder/shared/usePurchaseOrderRouting';
import PurchaseOrderTotalField from 'domain/finance/purchaseOrder/shared/field/PurchaseOrderTotalField';
import PurchasesListField from 'domain/finance/purchaseOrder/shared/field/PurchasesListField';
import InvoicesListField from 'domain/finance/purchaseOrder/shared/field/InvoicesListField';
import { LedgerLink } from 'domain/shared/ledger';
import { EmployeeLinkFieldWithNameLoading, OfficeCodeField } from 'domain/shared/employee';

const ViewPurchaseOrderPageContent = ({
  isSensitiveDataLoading = false,
  isSensitiveDataIsShown = false,
  toggleSensitiveDataButton = <></>,
}: {
  isSensitiveDataLoading?: boolean;
  isSensitiveDataIsShown?: boolean;
  toggleSensitiveDataButton?: React.ReactElement;
}) => {
  const id = usePurchaseOrderId();

  return (
    <ShowPageLayout
      entityHeader={<PurchaseOrderHeader />}
      breadcrumbs={<SinglePurchaseOrderBreadcrumbs />}
      headerButtons={[
        toggleSensitiveDataButton,
        <EditPurchaseOrderButton
          isVisible={(data: any) => data.getValueBySource('status') === PURCHASE_ORDER_STATUSES.PENDING}
        />,
        <CopyPurchaseOrderButton />,
        <ApprovePurchaseOrderButton statusSource="status" />,
        <ClosePurchaseOrderButton statusSource="status" />,
        <RejectPurchaseOrderButton statusSource="status" />,
      ]}
      hasSeparateSections
    >
      <ShowSection title="Main information">
        <SectionRow>
          <TextField label="Title" source="title" />
          <TextField label="Vendor" source="vendor" />
          <PurchaseOrderTypeField label="Type" source="type" />
          <OfficeCodeField source="office" />
        </SectionRow>
        <SectionRow>
          <LedgerLink label="Account" source="account" nameSource="account" />
          <TextField source="budgetCategory" nameSource="budgetCategory" label="Budget category" />
          <PurchaseOrderTotalField label="Total" source="subTotal" isVisible={isSensitiveDataIsShown} />
          <DateField
            label="Required delivery date"
            source="requiredDeliveryDate"
            isVisible={(data: any) => data.getValueBySource('type') === PURCHASE_ORDER_TYPES.STANDARD}
          />
          <DateField
            source="expirationDate"
            label="Expiration date"
            isVisible={(data: any) =>
              data.getValueBySource('type') === PURCHASE_ORDER_TYPES.BLANKET ||
              data.getValueBySource('type') === PURCHASE_ORDER_TYPES.CONTRACT
            }
          />
        </SectionRow>
        <SectionRow>
          <EnumField label="Status" source="status" options={PURCHASE_ORDER_STATUSES_OPTIONS} />
          <TextField
            label="Reject reason"
            source="rejectReason"
            isVisible={(data: any) => data.getValueBySource('status') === PURCHASE_ORDER_STATUSES.REJECTED}
          />
          <EmployeeLinkFieldWithNameLoading source="requestor" label="Requestor" />
          <EmployeeLinkFieldWithNameLoading
            source="approvedBy"
            label="Approved by"
            isVisible={(data: any) => !!data.getValueBySource('approvedBy')}
          />
          <EmployeeLinkFieldWithNameLoading
            source="rejectedBy"
            label="Rejected by"
            isVisible={(data: any) => !!data.getValueBySource('rejectedBy')}
          />
        </SectionRow>
        <BigSectionRow>
          <TextField label="Purchase purpose" source="purchasePurpose" />
        </BigSectionRow>
      </ShowSection>
      <ShowSection title="Purchases">
        <PurchasesListField source="purchases" isSensitiveDataIsShown={isSensitiveDataIsShown} />
      </ShowSection>
      <ShowSection>
        <InvoicesListField
          source="invoices"
          isSensitiveDataIsShown={isSensitiveDataIsShown || isSensitiveDataLoading}
        />
      </ShowSection>
      <ShowSection isVisible={isSensitiveDataIsShown || isSensitiveDataLoading}>
        <TransfersListField
          businessObjectId={id}
          businessObjectType={BUSINESS_OBJECT_TYPES.PURCHASE_ORDER}
          startDateSource="postedAt"
        />
      </ShowSection>
    </ShowPageLayout>
  );
};

export default ViewPurchaseOrderPageContent;
